import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { toast, confirmar } from '../../lib/toast';

type Row = {
  id: string;
  nombre: string;
  estado: string;
  importancia: 'URGENTE' | 'ALTA' | 'MEDIA' | 'BAJA';
  segmento: string | null;
  modelo_interno: string | null;
  pvpr_iva_eur: number | null;
  margin_target_pct: number | null;
  coste_target_usd: number | null;
  marca_id: string;
  marca_nombre: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  candidatos_count: number;
  created_at: string;
};

const ESTADOS = [
  { value: 'BUSCANDO', label: 'Buscando candidatos', color: '#94a3b8' },
  { value: 'CON_CANDIDATOS', label: 'Candidatos asociados', color: '#3b82f6' },
  { value: 'DECIDIDO', label: 'Ganador elegido', color: '#8b5cf6' },
  { value: 'CONVERTIDO', label: 'Convertido a producto', color: '#10b981' },
  { value: 'CANCELADO', label: 'Cancelado', color: '#ef4444' },
];

const IMPORTANCIAS = [
  { value: 'URGENTE', label: 'Urgente', color: '#dc2626' },
  { value: 'ALTA',    label: 'Alta',    color: '#ea580c' },
  { value: 'MEDIA',   label: 'Media',   color: '#0891b2' },
  { value: 'BAJA',    label: 'Baja',    color: '#64748b' },
];
const IMPORTANCIA_COLOR: Record<string, string> = Object.fromEntries(
  IMPORTANCIAS.map((i) => [i.value, i.color]),
);

type Props = {
  clienteCodigo?: string;
  title?: string;
  subtitle?: string;
};

export function NuevosProductosList({ clienteCodigo, title, subtitle }: Props = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const qs = clienteCodigo ? `?cliente_codigo=${encodeURIComponent(clienteCodigo)}` : '';

  const lista = useQuery({
    queryKey: ['nuevos-productos', 'listado-full', clienteCodigo ?? '*'],
    queryFn: () => api.get<{ data: Row[] }>(`/nuevos-productos/listado/full${qs}`),
  });
  const marcas = useQuery({
    queryKey: ['list', '/admin/marcas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; cliente_id: string }[] }>('/admin/marcas'),
  });
  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; gama_id: string }[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: '',
    marca_id: '',
    gama_id: '',
    categoria_id: '',
    subcategoria_id: '',
    segmento: '',
    modelo_interno: '',
    importancia: 'MEDIA' as 'URGENTE' | 'ALTA' | 'MEDIA' | 'BAJA',
  });

  // Preseleccionar marca por cliente cuando se sabe (ej. GAME).
  useEffect(() => {
    if (form.marca_id || !marcas.data?.data || !clienteCodigo) return;
    const marcaCliente = marcas.data.data.find((m) => m.nombre.toUpperCase() === clienteCodigo);
    if (marcaCliente) setForm((f) => ({ ...f, marca_id: marcaCliente.id }));
  }, [marcas.data, clienteCodigo, form.marca_id]);

  const categoriasForGama = useMemo(() => {
    const all = categorias.data?.data ?? [];
    if (!form.gama_id) return all;
    return all.filter((c) => c.gama_id === form.gama_id);
  }, [form.gama_id, categorias.data]);

  const subcatsForCategoria = useMemo(() => {
    const all = subcategorias.data?.data ?? [];
    if (!form.categoria_id) return all;
    return all.filter((s) => s.categoria_id === form.categoria_id);
  }, [form.categoria_id, subcategorias.data]);

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ id: string }>('/nuevos-productos', body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['nuevos-productos'] });
      setOpen(false);
      navigate(`/nuevos-productos/${data.id}`);
    },
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const recalcularTodos = useMutation({
    mutationFn: () => api.post<{ candidatos: number; actualizados: number; inviables: number; errores: number }>(
      '/nuevos-productos/recalcular-costes-target',
      {},
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nuevos-productos'] }),
  });
  const bulkDeleteMut = useMutation({
    mutationFn: () =>
      api.post<{ deleted: number }>('/nuevos-productos/bulk-delete', {
        ids: Array.from(selectedIds),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['nuevos-productos'] });
      setSelectedIds(new Set());
      toast.success(`${r.deleted} proyecto(s) eliminado(s)`);
    },
  });

  const columns: Column<Row>[] = [
    { key: 'marca_nombre', label: 'Marca', render: (r) => r.marca_nombre ?? '—', sortValue: (r) => r.marca_nombre ?? '' },
    { key: 'gama_nombre', label: 'Gama', render: (r) => r.gama_nombre ?? '—', sortValue: (r) => r.gama_nombre ?? '' },
    { key: 'categoria_nombre', label: 'Categoría', render: (r) => r.categoria_nombre ?? '—', sortValue: (r) => r.categoria_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subcategoría', render: (r) => r.subcategoria_nombre ?? '—', sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'detalle_nombre', label: 'Detalle', render: (r) => r.detalle_nombre ?? <span className="text-slate-300">—</span>, sortValue: (r) => r.detalle_nombre ?? '' },
    { key: 'segmento', label: 'Segmento', render: (r) => r.segmento ?? '—', sortValue: (r) => r.segmento ?? '' },
    { key: 'modelo_interno', label: 'Modelo interno', render: (r) => r.modelo_interno ?? '—', sortValue: (r) => r.modelo_interno ?? '' },
    {
      key: 'coste_target_usd',
      label: 'Coste target',
      align: 'right',
      render: (r) => <span className="font-semibold">{fmt.usd(r.coste_target_usd)}</span>,
      sortValue: (r) => Number(r.coste_target_usd),
    },
    { key: 'pvpr_iva_eur', label: 'PVPR', align: 'right', render: (r) => fmt.eur(r.pvpr_iva_eur), sortValue: (r) => Number(r.pvpr_iva_eur) },
    { key: 'margin_target_pct', label: 'Margin target', align: 'right', render: (r) => fmt.pct(r.margin_target_pct), sortValue: (r) => Number(r.margin_target_pct) },
    {
      key: 'candidatos_count',
      label: 'Prod. asociados',
      align: 'center',
      render: (r) => (r.candidatos_count > 0
        ? (
          <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
            {fmt.int(r.candidatos_count)}
          </span>
        )
        : <span className="inline-block h-1 w-6 rounded-full bg-red-500" title="Sin productos asociados" />),
      sortValue: (r) => r.candidatos_count,
    },
    {
      key: 'importancia',
      label: 'Importancia',
      render: (r) => (
        <Badge color={IMPORTANCIA_COLOR[r.importancia] ?? '#94a3b8'}>{r.importancia}</Badge>
      ),
      sortValue: (r) => {
        const order = { URGENTE: 1, ALTA: 2, MEDIA: 3, BAJA: 4 } as const;
        return order[r.importancia] ?? 5;
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title={title ?? 'Proyectos / Ideas'}
        subtitle={subtitle ?? 'Briefings de búsqueda: el qué queremos encontrar. Los candidatos se asocian desde productos de proveedor.'}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={recalcularTodos.isPending}
              onClick={() => recalcularTodos.mutate()}
              title="Recalcula el Coste Target de todos los proyectos con PVPR + Margin definidos."
            >
              {recalcularTodos.isPending ? 'Recalculando…' : '↻ Recalcular todos'}
            </Button>
            <Button variant="create" onClick={() => setOpen(true)}>+ Nuevo proyecto</Button>
          </>
        }
      />

      {selectedIds.size > 0 && (
        <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-sky-900">
              {selectedIds.size} proyecto{selectedIds.size === 1 ? '' : 's'} seleccionado{selectedIds.size === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                onClick={async () => {
                  if (!(await confirmar({ title: `¿Eliminar ${selectedIds.size} proyecto(s)?`, message: 'Se podrán recuperar desde base de datos.', confirmLabel: 'Eliminar', danger: true }))) return;
                  bulkDeleteMut.mutate();
                }}
                disabled={bulkDeleteMut.isPending}
              >
                {bulkDeleteMut.isPending ? 'Eliminando…' : '🗑 Eliminar'}
              </Button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
              >
                Limpiar selección
              </button>
            </div>
          </div>
        </div>
      )}

      <DataTable<Row>
        columns={columns}
        rows={lista.data?.data}
        loading={lista.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/nuevos-productos/${r.id}`)}
        defaultSort={{ key: 'importancia', desc: false }}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        filters={[
          { key: 'estado', label: 'Estado', options: ESTADOS.map((e) => ({ value: e.value, label: e.label })) },
          { key: 'importancia', label: 'Importancia', options: IMPORTANCIAS.map((i) => ({ value: i.value, label: i.label })) },
          { key: 'marca_id', label: 'Marca', options: (marcas.data?.data ?? []).map((m) => ({ value: m.id, label: m.nombre })) },
        ]}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nuevo proyecto"
        footer={
          <>
            <Button variant="danger" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              variant="create"
              onClick={() => create.mutate({
                nombre: form.nombre.trim() || form.modelo_interno.trim() || 'Sin nombre',
                marca_id: form.marca_id,
                gama_id: form.gama_id || null,
                categoria_id: form.categoria_id || null,
                subcategoria_id: form.subcategoria_id || null,
                segmento: form.segmento || null,
                modelo_interno: form.modelo_interno || null,
                importancia: form.importancia,
                estado: 'BUSCANDO',
              })}
              disabled={!form.marca_id || create.isPending || (!form.nombre && !form.modelo_interno)}
            >
              Crear y abrir
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          El resto de campos (specs, PVPR, margin target, coste target, ejemplos, imágenes) se rellenan en la ficha.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Marca" required>
            <Select
              options={(marcas.data?.data ?? []).map((m) => ({ value: m.id, label: m.nombre }))}
              value={form.marca_id}
              onChange={(e) => setForm((f) => ({ ...f, marca_id: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Importancia">
            <Select
              options={IMPORTANCIAS.map((i) => ({ value: i.value, label: i.label }))}
              value={form.importancia}
              onChange={(e) => setForm((f) => ({ ...f, importancia: e.target.value as Row['importancia'] }))}
            />
          </FormRow>
          <FormRow label="Gama">
            <Select
              options={[{ value: '', label: '—' }, ...(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))]}
              value={form.gama_id}
              emptyLabel={null}
              onChange={(e) => setForm((f) => ({ ...f, gama_id: e.target.value, categoria_id: '', subcategoria_id: '' }))}
            />
          </FormRow>
          <FormRow label="Categoría">
            <Select
              options={[{ value: '', label: '—' }, ...categoriasForGama.map((c) => ({ value: c.id, label: c.nombre }))]}
              value={form.categoria_id}
              emptyLabel={null}
              onChange={(e) => setForm((f) => ({ ...f, categoria_id: e.target.value, subcategoria_id: '' }))}
            />
          </FormRow>
          <FormRow label="Subcategoría">
            <Select
              options={[{ value: '', label: '—' }, ...subcatsForCategoria.map((s) => ({ value: s.id, label: s.nombre }))]}
              value={form.subcategoria_id}
              emptyLabel={null}
              onChange={(e) => setForm((f) => ({ ...f, subcategoria_id: e.target.value }))}
            />
          </FormRow>
          <FormRow label="Segmento">
            <Input value={form.segmento} onChange={(e) => setForm((f) => ({ ...f, segmento: e.target.value }))} />
          </FormRow>
          <div className="sm:col-span-2">
            <FormRow label="Modelo interno (servirá como nombre si no rellenas Nombre)">
              <Input value={form.modelo_interno} onChange={(e) => setForm((f) => ({ ...f, modelo_interno: e.target.value }))} />
            </FormRow>
          </div>
          <div className="sm:col-span-2">
            <FormRow label="Nombre (opcional, descriptivo)">
              <Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
            </FormRow>
          </div>
        </div>
      </Modal>
    </div>
  );
}
